import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    getFieldDefinitions,
    createFieldDefinition,
    updateFieldDefinition,
    deleteFieldDefinition,
    getAllSubdomains,
} from '../services/api.service';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const langs = ['fr', 'en', 'de'];
const flags = {
    fr: '/assets/images/fr.png',
    en: '/assets/images/en.png',
    de: '/assets/images/de.png',
};

const NO_SUBDOMAIN_KEY = '__NO_SUBDOMAIN__';
const getSubdomainValue = (subdomain) =>
    subdomain?.subdomain || subdomain?.key || subdomain?.slug || subdomain?._id || '';
const getSubdomainLabel = (subdomain) =>
    subdomain?.fullName || subdomain?.subdomain || subdomain?.name || subdomain?._id || '(Sans nom)';

const emptyI18n = () => ({ fr: '', en: '', de: '' });
const normalizeI18n = (obj) => ({ fr: '', en: '', de: '', ...(obj || {}) });

const defaultField = {
    key: '',
    category: 'general',
    type: 'text',
    enabled: true,
    order: 100,
    labels: emptyI18n(),
    descriptions: emptyI18n(),
    placeholder: emptyI18n(),
    validations: { required: false, pattern: '', min: undefined, max: undefined },
    options: [],
    dynamicOptionsToken: '',
    conditionalDisplay: undefined,
    subdomains: []
};

const KEY_RE = /^[a-z][a-z0-9_]*$/;

export default function FieldCatalogPage() {
    const [q, setQ] = useState('');
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [lang, setLang] = useState('fr');
    const [loading, setLoading] = useState(true);
    const [subdomains, setSubdomains] = useState([]);
    const [keyError, setKeyError] = useState('');
    const [subdomainOpen, setSubdomainOpen] = useState(false);
    const confirm = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const [fieldsData, subdomainsData] = await Promise.all([
                getFieldDefinitions(),
                getAllSubdomains(),
            ]);

            setList(Array.isArray(fieldsData) ? fieldsData : []);
            setSubdomains(Array.isArray(subdomainsData) ? subdomainsData : []);
        } catch {
            Helper.toast('Erreur lors du chargement des champs', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const t = useCallback(
        (i18n) => i18n?.[lang] || i18n?.fr || i18n?.en || i18n?.de || '',
        [lang]
    );

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return list;
        return list.filter(
            (f) =>
                (f.key || '').toLowerCase().includes(s) ||
                (t(f.labels) || '').toLowerCase().includes(s) ||
                (t(f.descriptions) || '').toLowerCase().includes(s) ||
                (f.category || '').toLowerCase().includes(s)
        );
    }, [list, q, t]); // 👈 depend on `t`

    const startNew = () => {
        setKeyError('');
        setSubdomainOpen(false);
        setEditing({ ...defaultField });
    };

    const startEdit = (f) => {
        const normalizedSubdomains = Array.isArray(f.subdomains)
            ? f.subdomains.map((value) => String(value)).filter(Boolean)
            : [];

        setKeyError('');
        setSubdomainOpen(normalizedSubdomains.length > 0);

        setEditing({
            ...f,
            labels: normalizeI18n(f.labels),
            descriptions: normalizeI18n(f.descriptions),
            placeholder: normalizeI18n(f.placeholder),
            conditionalDisplay: f.conditionalDisplay?.fieldKey
                ? f.conditionalDisplay
                : undefined,
            subdomains: normalizedSubdomains,
        });
    };

    const toggleSubdomain = (subdomainValue) => {
        setEditing((prev) => {
            const current = Array.isArray(prev.subdomains) ? prev.subdomains : [];
            const exists = current.includes(subdomainValue);

            return {
                ...prev,
                subdomains: exists
                    ? current.filter((value) => value !== subdomainValue)
                    : [...current, subdomainValue],
            };
        });
    };

    const setAllSubdomains = () => {
        setEditing((prev) => ({ ...prev, subdomains: [] }));
    };

    const cancel = () => setEditing(null);

    const onKeyChange = (v) => {
        const key = v.replace(/\s+/g, '_').toLowerCase();
        setEditing((prev) => ({ ...prev, key }));
        if (!KEY_RE.test(key)) {
            setKeyError("Format invalide. Utilisez a-z, 0-9 et '_' ; commencez par une lettre.");
        } else {
            setKeyError('');
        }
    };

    const enableConditionalDisplay = () => {
        setEditing((prev) => ({
            ...prev,
            conditionalDisplay: {
                fieldKey: '',
                operator: 'equals',
                value: '',
            },
        }));
    };

    const disableConditionalDisplay = () => {
        setEditing((prev) => ({
            ...prev,
            conditionalDisplay: undefined,
        }));
    };

    const updateConditionalDisplay = (patch) => {
        setEditing((prev) => ({
            ...prev,
            conditionalDisplay: {
                fieldKey: '',
                operator: 'equals',
                value: '',
                ...(prev.conditionalDisplay || {}),
                ...patch,
            },
        }));
    };

    const save = async () => {
        try {
            if (!editing.key) return Helper.toast('La clé est requise', 'error');
            if (!KEY_RE.test(editing.key)) {
                return Helper.toast('Clé invalide (a-z, 0-9, underscore, commence par une lettre).', 'error');
            }
            if (!editing.labels.fr && !editing.labels.en && !editing.labels.de) {
                return Helper.toast('Au moins un libellé i18n est requis', 'error');
            }

            const payload = {
                ...editing,
                conditionalDisplay: editing.conditionalDisplay?.fieldKey
                    ? editing.conditionalDisplay
                    : null,
                subdomains: Array.isArray(editing.subdomains)
                    ? editing.subdomains.filter(Boolean)
                    : [],
            };

            if (editing._id) {
                await updateFieldDefinition(editing._id, payload);
            } else {
                await createFieldDefinition(payload);
            }

            setEditing(null);
            load();
        } catch (e) {
            Helper.toast(e?.response?.data?.message || "Erreur lors de l'enregistrement", 'error');
        }
    };

    const remove = async (f) => {
        const ok = await confirm({
            title: 'Supprimer',
            message: (
                <>
                    Supprimer le champ "<strong>{f.labels['fr']}</strong>" ({f.key}) ?
                </>
            ),
            confirmText: 'Oui',
            cancelText: 'Non',
            tone: 'warning',
        });
        if (!ok) return;

        try {
            await deleteFieldDefinition(f._id);
            load();
        } catch {
            Helper.toast('Erreur lors de la suppression', 'error');
        }
    };

    const toggleEnabled = async (f) => {
        const next = { ...f, enabled: !f.enabled };
        setList((prev) => prev.map((x) => (x._id === f._id ? next : x)));
        try {
            await updateFieldDefinition(f._id, { enabled: next.enabled });
        } catch {
            setList((prev) => prev.map((x) => (x._id === f._id ? f : x)));
            Helper.toast("Impossible de mettre à jour l'état", 'error');
        }
    };

    const subdomainOptions = [
        { value: NO_SUBDOMAIN_KEY, label: 'Sans sous-domaine / domaine principal' },
        ...subdomains
            .map((subdomain) => ({
                value: getSubdomainValue(subdomain),
                label: getSubdomainLabel(subdomain),
            }))
            .filter((item) => item.value),
    ];

    // ------- UI -------
    return (
        <div className="max-w-6xl mx-auto p-6 space-y-4">
            {/* Sticky header with language switch */}
            <div className="sticky top-0 bg-white/90 backdrop-blur z-10 border-b border-gray-200 -mx-6 px-6 py-3 mb-2">
                <div className="flex items-center justify-between gap-3">
                    <h1 className="text-xl font-semibold">Catalogue des champs</h1>

                    <div className="flex items-center gap-3">
                        {/* Language Toggle (flags) */}
                        <div className="hidden sm:flex items-center gap-1 border rounded-xl bg-white p-1">
                            {langs.map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l)}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    title={l.toUpperCase()}
                                >
                                    <img src={flags[l]} alt={l} className="w-5 h-4 rounded-sm" />
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        <button onClick={startNew} className="inline-flex items-center gap-2 bg-primary text-white px-3 py-2 rounded shadow">
                            <FaPlus className="h-4 w-4" />
                            Ajouter un champ
                        </button>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="flex items-center gap-3">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher (clé, libellé, description, catégorie)"
                    className="border rounded px-3 py-2 w-full"
                />
            </div>

            {/* Table */}
            {loading ? (
                <div className="py-24 text-center text-gray-500">Chargement…</div>
            ) : (
                <div className="rounded border bg-white overflow-hidden">
                    <table className="min-w-full">
                        <thead className="bg-gray-50 text-left text-sm">
                            <tr>
                                <th className="p-2">Ordre</th>
                                <th className="p-2">Clé</th>
                                <th className="p-2">Type</th>
                                <th className="p-2">Catégorie</th>
                                <th className="p-2">Libellé ({lang.toUpperCase()})</th>
                                <th className="p-2">Activé</th>
                                <th className="p-2 w-44"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((f) => (
                                <tr key={f._id} className="border-t hover:bg-gray-50">
                                    <td className="p-2 text-sm text-gray-600">{f.order}</td>
                                    <td className="p-2 font-mono text-sm">{f.key}</td>
                                    <td className="p-2 text-sm">{f.type}</td>
                                    <td className="p-2 text-sm capitalize">{f.category}</td>
                                    <td className="p-2 text-sm truncate max-w-[280px]" title={t(f.labels)}>
                                        {t(f.labels)}
                                    </td>
                                    <td className="p-2">
                                        <button
                                            onClick={() => toggleEnabled(f)}
                                            className="inline-flex items-center gap-2 px-2 py-1 rounded border hover:bg-gray-50"
                                            aria-pressed={f.enabled}
                                            title={f.enabled ? 'Désactiver' : 'Activer'}
                                        >
                                            {f.enabled ? (
                                                <>
                                                    <FaToggleOn className="text-emerald-600" />
                                                    <span className="text-sm text-emerald-700">Oui</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FaToggleOff className="text-gray-400" />
                                                    <span className="text-sm text-gray-600">Non</span>
                                                </>
                                            )}
                                        </button>
                                    </td>
                                    <td className="p-2">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => startEdit(f)}
                                                className="px-2 py-1 text-sm rounded border inline-flex items-center gap-1"
                                            >
                                                <FaEdit /> Éditer
                                            </button>
                                            <button
                                                onClick={() => remove(f)}
                                                className="px-2 py-1 text-sm rounded border text-red-600 inline-flex items-center gap-1"
                                            >
                                                <FaTrash /> Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td className="p-6 text-center text-gray-500" colSpan={7}>
                                        Aucun résultat
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Drawer/Modal */}
            {editing && (
                <div
                    className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
                    onClick={cancel}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">
                                {editing._id ? 'Modifier' : 'Nouveau'} champ
                            </h2>
                            <button onClick={cancel} className="text-gray-500">
                                ✕
                            </button>
                        </div>

                        {/* Language Switch (same style as DocumentEditorPage) */}
                        <div className="mb-4">
                            <div className="inline-flex rounded-xl border bg-white p-1 shadow-sm">
                                {langs.map((l) => (
                                    <button
                                        key={l}
                                        onClick={() => setLang(l)}
                                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        <img src={flags[l]} alt={l} className="w-5 h-4 rounded-sm" />
                                        {l.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Grid: key / category / type */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="text-sm text-gray-600">
                                    Clé <span className="text-red-500">*</span>
                                </label>
                                <input
                                    value={editing.key}
                                    onChange={(e) => onKeyChange(e.target.value)}
                                    className={`w-full border rounded px-3 py-2 ${keyError ? 'border-red-300' : ''
                                        }`}
                                    placeholder="ex: first_name"
                                    disabled={!!editing._id}
                                />
                                {keyError && <p className="text-[11px] text-red-600 mt-1">{keyError}</p>}
                                {!keyError && (
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Identifiant unique (a-z, 0-9, « _ »). Immuable après création.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="text-sm text-gray-600">Catégorie</label>
                                <input
                                    value={editing.category}
                                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                    className="w-full border rounded px-3 py-2 capitalize"
                                    placeholder="ex: identite"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600">Type</label>
                                <select
                                    value={editing.type}
                                    onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    {[
                                        'text',
                                        'textarea',
                                        'email',
                                        'tel',
                                        'date',
                                        'number',
                                        'select',
                                        'checkbox',
                                        'file',
                                    ].map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Grid: order / enabled / required */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="text-sm text-gray-600">Ordre</label>
                                <input
                                    type="number"
                                    value={editing.order}
                                    onChange={(e) =>
                                        setEditing({ ...editing, order: Number(e.target.value || 0) })
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div className="flex items-center gap-2 mt-6">
                                <input
                                    id="enabled"
                                    type="checkbox"
                                    checked={!!editing.enabled}
                                    onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                                />
                                <label htmlFor="enabled" className="text-sm">
                                    Activé
                                </label>
                            </div>
                            <div className="flex items-center gap-2 mt-6">
                                <input
                                    id="required"
                                    type="checkbox"
                                    checked={!!editing.validations?.required}
                                    onChange={(e) =>
                                        setEditing({
                                            ...editing,
                                            validations: { ...editing.validations, required: e.target.checked },
                                        })
                                    }
                                />
                                <label htmlFor="required" className="text-sm">
                                    Requis (par défaut)
                                </label>
                            </div>
                        </div>

                        {/* Translated fields (Label / Placeholder / Description) */}
                        <div className="rounded-2xl border bg-white p-4 mb-4">
                            <h3 className="text-base font-semibold mb-3">
                                Texte affiché ({lang.toUpperCase()})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-gray-600">
                                        Libellé ({lang.toUpperCase()}) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        value={editing.labels?.[lang] || ''}
                                        onChange={(e) =>
                                            setEditing({
                                                ...editing,
                                                labels: { ...(editing.labels || {}), [lang]: e.target.value },
                                            })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-600">
                                        Placeholder ({lang.toUpperCase()})
                                    </label>
                                    <input
                                        value={editing.placeholder?.[lang] || ''}
                                        onChange={(e) =>
                                            setEditing({
                                                ...editing,
                                                placeholder: { ...(editing.placeholder || {}), [lang]: e.target.value },
                                            })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-600">
                                        Description ({lang.toUpperCase()})
                                    </label>
                                    <input
                                        value={editing.descriptions?.[lang] || ''}
                                        onChange={(e) =>
                                            setEditing({
                                                ...editing,
                                                descriptions: { ...(editing.descriptions || {}), [lang]: e.target.value },
                                            })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        S'affiche sous le champ pour guider l’utilisateur.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Type-specific options */}
                        {editing.type === 'select' && (
                            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-gray-600">
                                        Options (séparées par des virgules)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={(editing.options || []).join(', ')}
                                        onChange={(e) =>
                                            setEditing({
                                                ...editing,
                                                options: e.target.value
                                                    .split(',')
                                                    .map((v) => v.trim())
                                                    .filter(Boolean),
                                            })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-600">Token d'options dynamiques</label>
                                    <input
                                        value={editing.dynamicOptionsToken || ''}
                                        onChange={(e) =>
                                            setEditing({ ...editing, dynamicOptionsToken: e.target.value })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="ex: countries"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Laissez vide si vous utilisez les options statiques ci-contre.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Validations */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div>
                                <label className="text-sm text-gray-600">Regex (pattern)</label>
                                <input
                                    value={editing.validations?.pattern || ''}
                                    onChange={(e) =>
                                        setEditing({
                                            ...editing,
                                            validations: { ...editing.validations, pattern: e.target.value },
                                        })
                                    }
                                    className="w-full border rounded px-3 py-2 font-mono"
                                    placeholder="^\\d{14}$"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600">Min</label>
                                <input
                                    type="number"
                                    value={editing.validations?.min ?? ''}
                                    onChange={(e) =>
                                        setEditing({
                                            ...editing,
                                            validations: {
                                                ...editing.validations,
                                                min: e.target.value === '' ? undefined : Number(e.target.value),
                                            },
                                        })
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600">Max</label>
                                <input
                                    type="number"
                                    value={editing.validations?.max ?? ''}
                                    onChange={(e) =>
                                        setEditing({
                                            ...editing,
                                            validations: {
                                                ...editing.validations,
                                                max: e.target.value === '' ? undefined : Number(e.target.value),
                                            },
                                        })
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                        </div>

                        {/* Conditional display */}
                        <div className="rounded-2xl border bg-white p-4 mb-6">
                            <div className="flex items-center justify-between gap-3 mb-4">
                                <div>
                                    <h3 className="text-base font-semibold">Affichage conditionnel</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Permet d’afficher ce champ uniquement selon la réponse donnée à un autre champ.
                                    </p>
                                </div>

                                <label className="inline-flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!editing.conditionalDisplay?.fieldKey}
                                        onChange={(e) =>
                                            e.target.checked
                                                ? enableConditionalDisplay()
                                                : disableConditionalDisplay()
                                        }
                                    />
                                    Activer
                                </label>
                            </div>

                            {editing.conditionalDisplay && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-600">Champ déclencheur</label>
                                        <select
                                            value={editing.conditionalDisplay?.fieldKey || ''}
                                            onChange={(e) =>
                                                updateConditionalDisplay({ fieldKey: e.target.value })
                                            }
                                            className="w-full border rounded px-3 py-2"
                                        >
                                            <option value="">— Choisir un champ —</option>

                                            {list
                                                .filter((f) => f.key !== editing.key)
                                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                                .map((f) => (
                                                    <option key={f.key} value={f.key}>
                                                        {t(f.labels)} - {f.key}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-sm text-gray-600">Condition</label>
                                        <select
                                            value={editing.conditionalDisplay?.operator || 'equals'}
                                            onChange={(e) =>
                                                updateConditionalDisplay({ operator: e.target.value })
                                            }
                                            className="w-full border rounded px-3 py-2"
                                        >
                                            <option value="equals">Est égal à</option>
                                            <option value="not_equals">Est différent de</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-sm text-gray-600">Valeur attendue</label>
                                        <input
                                            value={editing.conditionalDisplay?.value || ''}
                                            onChange={(e) =>
                                                updateConditionalDisplay({ value: e.target.value })
                                            }
                                            className="w-full border rounded px-3 py-2"
                                            placeholder="ex: Oui"
                                        />
                                    </div>
                                </div>
                            )}

                            {editing.conditionalDisplay?.fieldKey && (
                                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
                                    Ce champ sera affiché si le champ{' '}
                                    <strong>{editing.conditionalDisplay.fieldKey}</strong>{' '}
                                    {editing.conditionalDisplay.operator === 'equals'
                                        ? 'est égal à'
                                        : 'est différent de'}{' '}
                                    <strong>{String(editing.conditionalDisplay.value || '')}</strong>.
                                </div>
                            )}
                        </div>

                        <details
                            className="rounded-2xl border bg-white mb-6 group"
                            open={subdomainOpen}
                            onToggle={(e) => setSubdomainOpen(e.currentTarget.open)}
                        >
                            <summary className="flex items-center justify-between gap-3 p-4 cursor-pointer list-none">
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border bg-gray-50 text-gray-600 transition-transform group-open:rotate-90">
                                        ›
                                    </span>

                                    <div>
                                        <h3 className="text-base font-semibold">Affichage selon le sous-domaine</h3>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Si aucune option n’est sélectionnée, le champ sera affiché partout.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setAllSubdomains();
                                        }}
                                        className={`text-xs rounded px-2 py-1 border ${(editing.subdomains || []).length === 0
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        Tous
                                    </button>
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                        {(editing.subdomains || []).length} sélectionné(s)
                                    </span>
                                </div>
                            </summary>

                            <div className="px-4 pb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                                    {subdomainOptions.map((option) => {
                                        const checked = (editing.subdomains || []).includes(option.value);

                                        return (
                                            <label
                                                key={option.value}
                                                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 cursor-pointer ${checked
                                                    ? 'bg-blue-50 border-blue-400 text-blue-800'
                                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium truncate">
                                                        {option.label}
                                                    </div>
                                                    {option.value !== NO_SUBDOMAIN_KEY && (
                                                        <div className="text-xs text-gray-500 truncate">
                                                            {option.value}
                                                        </div>
                                                    )}
                                                </div>

                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleSubdomain(option.value)}
                                                    className="h-4 w-4"
                                                />
                                            </label>
                                        );
                                    })}
                                </div>

                                {(editing.subdomains || []).length > 0 && (
                                    <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
                                        Ce champ sera affiché uniquement pour les emplacements sélectionnés.
                                    </div>
                                )}
                            </div>
                        </details>

                        <div className="flex items-center justify-end gap-2">
                            <button onClick={cancel} className="px-4 py-2 rounded border">
                                Annuler
                            </button>
                            <button
                                onClick={save}
                                disabled={
                                    !editing?.key ||
                                    !!keyError ||
                                    (!editing?.labels?.fr && !editing?.labels?.en && !editing?.labels?.de)
                                }
                                className={`px-4 py-2 rounded text-white ${!editing?.key ||
                                    !!keyError ||
                                    (!editing?.labels?.fr && !editing?.labels?.en && !editing?.labels?.de)
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}